import _ from 'lodash';
import moment from 'moment';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';

import i18n from '../../config/i18n';
import Link from '../../components/shared/Link';
import connectActionsAndReducers from '../../toolbox/connectActionsAndReducers';
import roleFields from './RoleFieldsMapping';
import {
  FIRST_NAME,
  LAST_NAME,
  PHONE,
  D_O_B,
  SSN,
  TITLE_ID,
  SPECIALTY_ID,
  MEDICAL_GROUP_ID,
} from '../../constants/ProfileFields';

import Image from '../../components/shared/Image';

// default avatar images
import defaultAvatarImage from '../../styles/images/no_avatar_image.png';

const styles = {
  root: { padding: 0 },
  inner: { padding: '20px 30px', marginBottom: 0 },
  form: { marginTop: 25 },
  link: { textDecoration: 'none' },
  avatar: {
    container: {
      maxWidth: 400,
      minHeight: 400,
      position: 'relative',
      margin: '10px auto',
    },
    image: {
      width: 400,
      height: 400,
      objectFit: 'cover',
    },
    hint: {
      color: '#d52626',
      fontSize: '18px',
      marginTop: '12px',
      textAlign: 'center',
    },
  },
  button: {
    editProfile: {
    },
  },
  slot: {
    group: {
      display: 'flex',
      height: '45px',
    },
    title: {
      color: 'gray',
      fontSize: 18,
      fontWeight: 'bold',
      textAlign: 'right',
      lineHeight: '45px',
    },
    value: {
      fontSize: 18,
      lineHeight: '45px',
    },
    required: {
      position: 'relative',
      content: '*',
      color: 'red',
      right: 5,
      top: 5,
    },
    row: {
      verticalAlign: 'top',
      height: '60px',
    },
  },
  table: { width: '100%' },
  profile: { margin: '0px 15px' },
};

const ProfileContainer = ({
  actions,
  user: { profile },
  metadata,
  router,
}) => {
  // Retrieve user profile one time when mounted.
  useEffect(() => {
    // Fetch metadata
    if (_.isEmpty(metadata.data)) {
      actions.getMetadata();
    }

    // retrieve user profile
    actions.getUserProfile();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build up form rows components
  let formBlock;
  if ((profile && profile.id != null) && _.keys(metadata).length > 0) {
    const titlesData = metadata.data.title || [];
    const specialtiesData = metadata.data.specialty || [];
    const medicalGroups = metadata.data.medicalGroup || [];
    const title = titlesData
      .find(({ id }) => id === profile.titleId) || {};
    const specialty = specialtiesData
      .find(({ id }) => id === profile.specialtyId) || {};
    const group = medicalGroups
      .find(({ id }) => id === profile.medicalGroupId) || {};
    const allAvailableSlots = {
      [FIRST_NAME]: () => (
        { title: i18n.t('firstName'), value: profile.firstName }
      ),
      [LAST_NAME]: () => (
        { title: i18n.t('lastName'), value: profile.lastName }
      ),
      [PHONE]: () => (
        { title: i18n.t('phoneNumber'), value: profile.phone }
      ),
      [D_O_B]: () => (
        { title: i18n.t('d_o_b'), value: profile.dateOfBirth ? moment(profile.dateOfBirth).format('YYYY-MM-DD') : '' }
      ),
      [SSN]: () => (
        { title: i18n.t('ssn'), value: profile.ssn }
      ),
      [TITLE_ID]: () => (
        { title: i18n.t('title'), value: title.name }
      ),
      [SPECIALTY_ID]: () => (
        { title: i18n.t('specialty'), value: specialty.name }
      ),
      [MEDICAL_GROUP_ID]: () => (
        { title: i18n.t('group'), required: true, value: group.name }
      ),
    };

    // Filter out necessary fields for current role
    const formSlotData = profile.roleId &&
    roleFields[profile.roleId].fields.map(fieldName => allAvailableSlots[fieldName]());

    formBlock = formSlotData.map((data) => {
      const requiredBlock = data.required ? (<span style={styles.slot.required}>*</span>) : null;
      return (
        <tr key={uuid()} style={styles.slot.row}>
          <td className="col-xs-6 col-sm-6 col-md-4" style={styles.slot.title}>
            {requiredBlock}{data.title}
          </td>
          <td className="col-xs-6 col-sm-6 col-md-8" style={styles.slot.value}>
            {data.value}
          </td>
        </tr>
      );
    });
  }

  return (
    <div className="Profile" style={styles.profile}>
      <h1>
        {i18n.t('myProfile')}
      </h1>
      <div className="col-sm-12 col-md-12" style={styles.root}>
        <div className="well well-md" style={styles.inner}>
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <div className="avatarContainer" style={styles.avatar.container}>
                <Image
                  src={profile.avatar || ''}
                  alt={i18n.t('avatar')}
                  style={styles.avatar.image}
                  defaultImage={defaultAvatarImage}
                  className="img-rounded img-responsive center-block"
                />
              </div>
              <div style={styles.avatar.hint}>
                {i18n.t('avatarHint')}
              </div>
            </div>
            <div className="col-xs-12 col-sm-12 col-md-6" style={styles.form}>
              <table style={styles.table}>
                <tbody>
                  {formBlock}
                </tbody>
              </table>
              <div className="form-group" style={styles.slot.group}>
                <div className="col-xs-12 col-sm-8 col-md-12 center-block">
                  <Link href="/profile/edit" style={styles.link} router={router}>
                    <button
                      type="button"
                      className="btn btn-primary pull-right"
                      style={styles.button.editProfile}
                    >
                      {i18n.t('editProfile')}
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ProfileContainer.propTypes = {
  actions: PropTypes.shape({
    getMetadata: PropTypes.func.isRequired,
    getUserProfile: PropTypes.func.isRequired,
  }).isRequired,
  user: PropTypes.object.isRequired,
  file: PropTypes.shape({
    photoUrl: PropTypes.string.isRequired,
  }).isRequired,
  metadata: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
};

export default connectActionsAndReducers(ProfileContainer);



// WEBPACK FOOTER //
// ./src/containers/profile/ProfileContainer.js